import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useApi } from '../lib/use-api';
import { api, shortDate, relativeTime } from '../lib/api';
import { ErrorBanner, Loading, Panel, Status, Empty } from '../components/ui';

interface DeviceView {
  deviceId: string;
  status: string;
  serialNumber: string | null;
  hardwareRevision: string | null;
  firmwareVersion: string | null;
  locationId: string | null;
  businessGroupId: string | null;
  warranty: { startDate: string | null; termMonths: number | null; expiresAt: string | null; active: boolean };
  replacesDeviceId: string | null;
  replacedByDeviceId: string | null;
  lastCheckinAt: string | null;
  registeredAt: string | null;
}

interface History {
  statusHistory: Array<{ from_status: string | null; to_status: string; actor_id: string | null; reason: string | null; created_at: string }>;
  ownershipHistory: Array<{ owner_type: string; owner_name: string | null; acquired_at: string; released_at: string | null }>;
}

interface Command {
  id: string;
  command_type: string;
  status: string;
  created_at: string;
  last_error: string | null;
}

/** Wireframe C2 detail — ownership history, warranty, firmware, actions. */
export function DeviceDetail() {
  const { deviceId = '' } = useParams();
  const device = useApi<DeviceView>(`/internal/devices/${deviceId}`);
  const history = useApi<History>(`/internal/devices/${deviceId}/history`);
  const commands = useApi<{ items: Command[] }>(`/internal/devices/${deviceId}/commands`);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<unknown>(null);

  async function transition(status: string, reason: string) {
    setBusy(true);
    setActionError(null);
    try {
      await api.post(`/internal/devices/${deviceId}/transitions`, { status, reason });
      device.reload();
      history.reload();
      commands.reload();
    } catch (caught) {
      setActionError(caught);
    } finally {
      setBusy(false);
    }
  }

  if (device.loading) return <Loading />;
  if (device.error) return <ErrorBanner error={device.error} />;
  if (!device.data) return <Empty>Device not found.</Empty>;

  const d = device.data;

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="mono">{d.deviceId}</h1>
          <p>
            <Status value={d.status} /> · Firmware {d.firmwareVersion ?? 'unknown'} · Last check-in{' '}
            {relativeTime(d.lastCheckinAt)}
          </p>
        </div>
        <div className="actions">
          <button className="secondary" disabled={busy || d.status !== 'active'} onClick={() => transition('idle', 'Marked idle from console')}>
            Mark idle
          </button>
          <button className="secondary" disabled={busy || d.status !== 'idle'} onClick={() => transition('active', 'Returned to service')}>
            Mark active
          </button>
          <button
            className="danger"
            disabled={busy || !['registered', 'active', 'idle'].includes(d.status)}
            onClick={() => transition('released', 'Released from console')}
          >
            Release / sell
          </button>
        </div>
      </div>

      <ErrorBanner error={actionError} />

      <div className="panel">
        <div className="panel-body">
          <dl className="detail-grid">
            <div>
              <dt>Serial number</dt>
              <dd className="mono">{d.serialNumber ?? '—'}</dd>
            </div>
            <div>
              <dt>Hardware revision</dt>
              <dd>{d.hardwareRevision ?? '—'}</dd>
            </div>
            <div>
              <dt>Warranty</dt>
              <dd>
                {d.warranty.expiresAt ? (
                  <>
                    <Status value={d.warranty.active ? 'active' : 'retired'} /> until {shortDate(d.warranty.expiresAt)}{' '}
                    <span className="muted">({d.warranty.termMonths} months)</span>
                  </>
                ) : (
                  '—'
                )}
              </dd>
            </div>
            <div>
              <dt>Registered</dt>
              <dd>{shortDate(d.registeredAt)}</dd>
            </div>
            {d.replacesDeviceId ? (
              <div>
                <dt>Replaces</dt>
                <dd className="mono">{d.replacesDeviceId}</dd>
              </div>
            ) : null}
            {d.replacedByDeviceId ? (
              <div>
                <dt>Replaced by</dt>
                <dd className="mono">{d.replacedByDeviceId}</dd>
              </div>
            ) : null}
          </dl>
        </div>
      </div>

      <Panel title="Ownership history">
        {history.data?.ownershipHistory.length ? (
          <table>
            <thead>
              <tr>
                <th>Owner</th>
                <th>Type</th>
                <th>Acquired</th>
                <th>Released</th>
              </tr>
            </thead>
            <tbody>
              {history.data.ownershipHistory.map((row, index) => (
                <tr key={index}>
                  <td>{row.owner_name ?? '—'}</td>
                  <td className="muted">{row.owner_type.replace(/_/g, ' ')}</td>
                  <td>{shortDate(row.acquired_at)}</td>
                  <td>{row.released_at ? shortDate(row.released_at) : <span className="badge ok">current</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <Empty>No ownership recorded yet.</Empty>
        )}
      </Panel>

      <Panel title="Lifecycle history">
        {history.data?.statusHistory.length ? (
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Event</th>
                <th>By</th>
                <th>Reason</th>
              </tr>
            </thead>
            <tbody>
              {history.data.statusHistory.map((row, index) => (
                <tr key={index}>
                  <td>{shortDate(row.created_at)}</td>
                  <td>
                    {row.from_status ? <span className="muted">{row.from_status.replace(/_/g, ' ')} → </span> : null}
                    <Status value={row.to_status} />
                  </td>
                  <td className="muted">{row.actor_id ?? '—'}</td>
                  <td className="muted">{row.reason ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <Empty>No history.</Empty>
        )}
      </Panel>

      <Panel title="Command queue">
        {commands.data?.items.length ? (
          <table>
            <thead>
              <tr>
                <th>Command</th>
                <th>Status</th>
                <th>Queued</th>
                <th>Error</th>
              </tr>
            </thead>
            <tbody>
              {commands.data.items.slice(0, 20).map((command) => (
                <tr key={command.id}>
                  <td>{command.command_type.replace(/_/g, ' ')}</td>
                  <td>
                    <Status value={command.status} />
                  </td>
                  <td className="muted">{relativeTime(command.created_at)}</td>
                  <td className="muted">{command.last_error ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <Empty>Nothing queued for this device.</Empty>
        )}
      </Panel>
    </>
  );
}
